// Resolves the public URL for an attachment / avatar / arbitrary R2 object.
//
// Today the server writes the absolute URL into `attachments.storage_url` /
// `users.avatar_url`. That works until R2 changes (custom domain, region,
// bucket rename) — at which point every existing image breaks because its
// absolute URL is now stale. The fix: store the relative key, compute the
// public URL at render time. WS-A1 in the production-hardening plan.
//
// Strategy:
//
// - If a public URL is provided AND the record has `storage_key`, prefer
//   the computed URL. This is the future-proof path.
// - Otherwise fall back to the legacy absolute URL on the record. Keeps
//   every renderer working today before `VITE_R2_PUBLIC_URL` is wired up
//   and before the schema/server flip.
//
// Once `VITE_R2_PUBLIC_URL` is configured in `.env.staging` / `.env.production`
// and the migration drops the NOT NULL on `storage_url`, every renderer is
// already going through this helper and will transparently switch to the
// relative-key path. No follow-up renderer edits required.

export interface AttachmentLike {
  storage_key?: string | null;
  storage_url?: string | null;
}

function readPublicUrlFromEnv(): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = (import.meta as any).env?.VITE_R2_PUBLIC_URL as
    | string
    | undefined;
  return (raw ?? "").trim().replace(/\/$/, "");
}

/**
 * Resolve an attachment's public URL.
 *
 * Pass `publicUrl` to override the env-read base (useful for tests). In
 * normal use, callers omit it and the helper reads `VITE_R2_PUBLIC_URL`.
 *
 * Returns "" when the record has neither a `storage_key` (with a public
 * base set) nor a `storage_url`. Renderers should treat empty string as
 * "no source" and avoid emitting a broken `<img src="">`.
 */
export function resolveAttachmentUrl(
  att: AttachmentLike,
  publicUrl?: string,
): string {
  const baseRaw = publicUrl !== undefined ? publicUrl : readPublicUrlFromEnv();
  const base = baseRaw.trim().replace(/\/$/, "");
  if (base && att.storage_key) {
    return `${base}/${att.storage_key.replace(/^\/+/, "")}`;
  }
  return att.storage_url?.trim() ?? "";
}

/**
 * Resolve an avatar URL. Avatars currently only have an absolute URL column
 * (`users.avatar_url`); when WS-A1 adds `users.avatar_key`, this helper will
 * grow a key-preferred branch matching `resolveAttachmentUrl` above.
 */
export function resolveAvatarUrl(
  user: { avatar_url?: string | null } | null | undefined,
): string | null {
  const url = user?.avatar_url?.trim();
  return url && url.length > 0 ? url : null;
}
